import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Loader2, TrendingDown, HelpCircle } from 'lucide-react';
import { Market, MarketOption, UserProfile } from '../types.ts';
import { useSettings } from '../lib/settings/settingsContext.tsx';
import { supabase } from '../lib/db.ts';
import { logger } from '../services/logger.ts';
import { sellLMSRShares } from '../lib/api.ts';
import { lmsrGrossSellProceeds, lmsrPrice } from '../lib/lmsrMath.ts';
import { formatRateAsPercent, performanceFeeRateOnProfit } from '../lib/sharePricedFees.ts';
import { formatSharePriceZar, formatSharesExact2 } from '../lib/displayFormat.ts';
import { useMinTradeZar } from '../hooks/useMinTradeZar.ts';
import { isLmsrPartialSellBlockedByMinIntegral } from '../lib/tradeLimits.ts';
import { blendedBasisSold, type LmsrBetBasisRow } from '../lib/lmsrSellBasis.ts';
import { friendlyLmsrSellError } from '../lib/lmsrSellErrors.ts';

function InfoTooltip({ label, content }: { label: string; content: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        aria-label={label}
        aria-describedby={open ? 'sell-info-tooltip' : undefined}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center rounded-full p-0.5 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-wayja-teal/50"
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <span
          id="sell-info-tooltip"
          role="tooltip"
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-64 rounded-md bg-gray-900 px-3 py-2 text-xs font-normal text-white shadow-lg pointer-events-none"
        >
          {content}
          <span className="absolute left-1/2 top-full -translate-x-1/2 -mt-px h-2 w-2 rotate-45 bg-gray-900" />
        </span>
      )}
    </span>
  );
}


interface LMSRSellPanelProps {
  market: Market;
  userProfile: UserProfile | null;
  selectedOptionId: number | null;
  onCancel: () => void;
  onBetSuccess?: () => void | Promise<void>;
}

type BetFifoRow = LmsrBetBasisRow & { created_at: string };

function mergeOutstandingShares(
  prev: MarketOption[],
  rows: { id: number; outstanding_shares?: number | null }[],
): MarketOption[] {
  return prev.map((o) => {
    const row = rows.find((r) => r.id === o.id);
    if (!row || row.outstanding_shares === undefined || row.outstanding_shares === null) return o;
    return { ...o, outstanding_shares: Number(row.outstanding_shares) };
  });
}

export function LMSRSellPanel({ market, userProfile, selectedOptionId, onCancel, onBetSuccess }: LMSRSellPanelProps) {
  const { formatCurrency } = useSettings();
  const minTradeZar = useMinTradeZar();
  const [sharesInput, setSharesInput] = useState('');
  const [isSelling, setIsSelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [ownedShares, setOwnedShares] = useState(0);
  const [fifoRows, setFifoRows] = useState<BetFifoRow[]>([]);
  const [liveOptions, setLiveOptions] = useState<MarketOption[]>(() => market.market_options ?? []);

  const performanceFeeOnProfit = useMemo(() => performanceFeeRateOnProfit(market), [market]);

  useEffect(() => {
    setLiveOptions(market.market_options ?? []);
  }, [market.market_options]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const role = data?.user?.app_metadata?.role;
      setIsAdmin(role === 'admin' || role === 'superuser');
    });
  }, []);

  useEffect(() => {
    const mid = market.id;
    const refreshSharesFromDb = async () => {
      const { data, error: qErr } = await supabase
        .from('market_options')
        .select('id, outstanding_shares')
        .eq('market_id', mid)
        .eq('status', 'active');
      if (qErr || !data) return;
      setLiveOptions((prev) => mergeOutstandingShares(prev, data));
    };

    const channel = supabase
      .channel(`lmsr-sell-${mid}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'market_options', filter: `market_id=eq.${mid}` },
        (payload) => {
          if (payload.new && typeof payload.new === 'object' && 'id' in payload.new) {
            const row = payload.new as Partial<MarketOption> & { id: number };
            setLiveOptions((prev) => prev.map((o) => (o.id === row.id ? { ...o, ...row } : o)));
          }
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_bets', filter: `market_id=eq.${mid}` },
        () => {
          void refreshSharesFromDb();
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [market.id]);

  const loadOwnership = useCallback(async () => {
    if (!selectedOptionId) {
      setOwnedShares(0);
      setFifoRows([]);
      return;
    }
    const { data: sessionData } = await supabase.auth.getSession();
    const uid = sessionData?.session?.user?.id;
    if (!uid) {
      setOwnedShares(0);
      setFifoRows([]);
      return;
    }
    const { data, error: qErr } = await supabase
      .from('user_bets')
      .select('shares_owned, amount, amount_after_fee, created_at')
      .eq('user_id', uid)
      .eq('market_id', market.id)
      .eq('market_option_id', selectedOptionId)
      .eq('status', 'active')
      .gt('shares_owned', 0)
      .order('created_at', { ascending: true });
    if (qErr || !data) {
      setOwnedShares(0);
      setFifoRows([]);
      return;
    }
    const rows = data as BetFifoRow[];
    setFifoRows(rows);
    setOwnedShares(rows.reduce((s, r) => s + Number(r.shares_owned ?? 0), 0));
  }, [market.id, selectedOptionId]);

  useEffect(() => {
    void loadOwnership();
  }, [loadOwnership]);

  const options = liveOptions;
  const b = market.lmsr_b ?? 100;
  const sharesVec = useMemo(() => options.map((o) => Number(o.outstanding_shares ?? 0)), [options]);
  const selectedOption = options.find((o) => o.id === selectedOptionId);
  const optionIdx = options.findIndex((o) => o.id === selectedOptionId);

  const sellQty = Math.min(Math.max(parseFloat(sharesInput) || 0, 0), ownedShares);

  const [sellQuote, setSellQuote] = useState<{
    finalProceeds: number;
    integralProceeds?: number;
  } | null>(null);

  useEffect(() => {
    if (!selectedOptionId || sellQty <= 0 || optionIdx < 0 || !market.id) {
      setSellQuote(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase.rpc('quote_lmsr_sell', {
        p_market_id: market.id,
        p_market_option_id: selectedOptionId,
        p_shares: sellQty,
      });
      if (cancelled) return;
      if (error || !data || (data as { success?: boolean }).success === false) {
        setSellQuote(null);
        return;
      }
      const row = data as { final_proceeds?: unknown; integral_proceeds?: unknown };
      const fp = Number(row.final_proceeds);
      const ip = Number(row.integral_proceeds);
      setSellQuote({
        finalProceeds: Number.isFinite(fp) ? Math.max(0, fp) : 0,
        integralProceeds: Number.isFinite(ip) ? Math.max(0, ip) : undefined,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [market.id, selectedOptionId, sellQty, optionIdx]);

  const grossProceeds = useMemo(() => {
    if (optionIdx < 0 || sellQty <= 0) return 0;
    return lmsrGrossSellProceeds(b, sharesVec, optionIdx, sellQty);
  }, [b, sharesVec, optionIdx, sellQty]);

  const grossIntegralForMin =
    sellQuote?.integralProceeds != null && Number.isFinite(sellQuote.integralProceeds)
      ? sellQuote.integralProceeds
      : grossProceeds;
  /** Backend matches integral gross vs configurable floor; full exit can be under that floor. */
  const belowMinSellIntegral = isLmsrPartialSellBlockedByMinIntegral(
    sellQty,
    grossIntegralForMin,
    ownedShares,
    minTradeZar,
  );

  const tradeProceeds =
    sellQuote != null && Number.isFinite(sellQuote.finalProceeds)
      ? Math.max(0, sellQuote.finalProceeds)
      : grossProceeds;

  const basisSold = useMemo(() => blendedBasisSold(fifoRows, sellQty), [fifoRows, sellQty]);
  const projectedPnL = Math.round((tradeProceeds - basisSold) * 100) / 100;
  const profitForFee = Math.max(0, projectedPnL);
  const feePreview = Math.round(profitForFee * performanceFeeOnProfit * 100) / 100;
  const netPreview = Math.round((tradeProceeds - feePreview) * 100) / 100;

  const currentPrice =
    optionIdx >= 0 && sharesVec.length > 0 ? lmsrPrice(b, sharesVec, optionIdx) : 1 / Math.max(options.length, 2);

  // Blended average buy price (net stake): SUM(COALESCE(amount_after_fee, amount)) / SUM(shares_owned).
  const avgBuyPrice = useMemo(() => {
    const totalPaid = fifoRows.reduce((s, r) => s + Number(r.amount_after_fee ?? r.amount ?? 0), 0);
    const totalShares = fifoRows.reduce((s, r) => s + Number(r.shares_owned ?? 0), 0);
    return totalShares > 0 ? totalPaid / totalShares : 0;
  }, [fifoRows]);
  const estPnLOnSale = projectedPnL;
  const sharesRemaining = Math.max(0, ownedShares - sellQty);

  const handleSell = async () => {
    if (!selectedOption || sellQty <= 0) return;
    if (sellQty > ownedShares) {
      setError('You cannot sell more shares than you own on this outcome.');
      return;
    }
    setError(null);
    setSuccess(null);
    setIsSelling(true);
    try {
      const maxProceeds = Math.round((tradeProceeds + 0.02) * 100) / 100;
      const result = await sellLMSRShares({
        option_id: selectedOption.id,
        shares: sellQty,
        market_id: market.id,
        max_cost: maxProceeds > 0 ? maxProceeds : undefined,
      });
      const net = result.net_proceeds ?? netPreview;
      setSuccess(
        `Sold ${formatSharesExact2(sellQty)} shares. ${formatCurrency(Number(net))} added to your wallet.`,
      );
      setSharesInput('');
      await loadOwnership();
      if (onBetSuccess) await onBetSuccess();
    } catch (err: unknown) {
      logger.error('LMSR sell error:', err);
      setError(friendlyLmsrSellError(err));
    } finally {
      setIsSelling(false);
    }
  };

  if (market.status !== 'active') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <p className="text-gray-500 text-sm">This market is not open for selling.</p>
        <button type="button" onClick={onCancel} className="mt-3 px-4 py-2 bg-gray-200 rounded-md text-sm">
          Go Back
        </button>
      </div>
    );
  }

  const isSellDisabled =
    !selectedOption ||
    sellQty <= 0 ||
    isSelling ||
    isAdmin ||
    ownedShares <= 0 ||
    sellQty > ownedShares ||
    belowMinSellIntegral;

  return (
    <div className="bg-white border border-wayja-teal/30 rounded-lg p-5 relative z-30 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-wayja-light-teal/20 text-wayja-dark-teal border border-wayja-light-teal/50">
          <TrendingDown className="w-3 h-3" />
          Sell shares
        </span>
        <h3 className="text-xl font-medium text-gray-900">Reduce your position</h3>
      </div>

      {isAdmin && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-blue-700 text-sm">
          <strong>Admin Notice:</strong> Admins cannot trade.
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
      )}
      {belowMinSellIntegral && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-amber-900 text-sm" role="alert">
          Minimum trade size is R{minTradeZar} gross on partial sells. Sell your full position to exit a smaller
          holding.
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
          {success}
        </div>
      )}

      <div className="mb-5 p-4 bg-wayja-light-teal/20 border border-wayja-light-teal/50 rounded-md">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Selected outcome</p>
            <p className="font-medium text-lg">{selectedOption?.name ?? 'None selected'}</p>
          </div>
          {selectedOption && (
            <div className="text-right">
              <p className="text-xs text-gray-500 mb-0.5">Current price</p>
              <p className="font-bold text-wayja-dark-teal text-lg">
                R{formatSharePriceZar(currentPrice)}/share
              </p>
              <p className="text-xs text-gray-500 mt-0.5">You own {formatSharesExact2(ownedShares)} shares</p>
            </div>
          )}
        </div>
      </div>

      {ownedShares <= 0 && selectedOption && !isAdmin && (
        <p className="mb-4 text-sm text-gray-600">You have no shares to sell on this outcome.</p>
      )}

      <div className="mb-5">
        <div className="flex justify-between items-center mb-2">
          <label className="text-base font-medium text-gray-900">Shares to sell</label>
          <span className="text-sm text-gray-600">
            Wallet: <span className="font-semibold">{formatCurrency(userProfile?.wallet_balance ?? 0)}</span>
          </span>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3 shadow-sm focus-within:ring-2 focus-within:ring-wayja-teal/50">
          <div className="flex items-center justify-between gap-2">
            <input
              type="number"
              className="bg-transparent text-gray-900 text-right text-2xl w-full focus:outline-none placeholder-gray-400"
              value={sharesInput}
              onChange={(e) => {
                setSharesInput(e.target.value);
                setError(null);
              }}
              min="0"
              step="0.0001"
              disabled={isSelling || ownedShares <= 0}
              placeholder="0.0000"
            />
            <span className="text-gray-500 text-sm whitespace-nowrap">of {formatSharesExact2(ownedShares)} shares</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setSharesInput((ownedShares / 2).toFixed(2))}
            disabled={isSelling || ownedShares <= 0}
            className="bg-white hover:bg-gray-50 text-gray-700 py-2 px-3 rounded-lg text-sm font-medium border border-gray-200 disabled:opacity-50"
          >
            Sell half
          </button>
          <button
            type="button"
            onClick={() => setSharesInput(ownedShares.toFixed(2))}
            disabled={isSelling || ownedShares <= 0}
            className="bg-white hover:bg-gray-50 text-wayja-teal py-2 px-3 rounded-lg text-sm font-medium border border-gray-200 disabled:opacity-50"
          >
            Sell all
          </button>
        </div>
      </div>

      {selectedOption && sellQty > 0 && (
        <div className="mb-5 bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="inline-flex items-center gap-1 text-gray-800">
              Price per share right now
              <InfoTooltip
                label="Current share price help"
                content="Marginal probability price for display only. Actual sale uses the full LMSR liquidation quote (curve + spread)."
              />
            </span>
            <span className="font-semibold text-gray-900">{formatCurrency(currentPrice)}</span>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>500 shares at this price would be worth</span>
            <span>{formatCurrency(currentPrice * 500)}</span>
          </div>

          <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
            <span className="text-gray-800">You are selling</span>
            <span className="font-semibold text-gray-900">{formatSharesExact2(sellQty)} shares</span>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>What you paid for these shares</span>
            <span>{formatCurrency(basisSold)}</span>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>Your avg. buy price</span>
            <span>R{formatSharePriceZar(avgBuyPrice)} / share</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Estimated P/L on this sale</span>
            <span className={`font-medium ${estPnLOnSale >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {estPnLOnSale >= 0 ? '+' : '-'}{formatCurrency(Math.abs(estPnLOnSale))}
            </span>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>Shares remaining after sale</span>
            <span>{formatSharesExact2(sharesRemaining)}</span>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>Est. sale proceeds (liquidation quote)</span>
            <span>{formatCurrency(tradeProceeds)}</span>
          </div>

          <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
            <span className="inline-flex items-center gap-1 text-gray-800">
              Estimated profit
              <InfoTooltip
                label="Profit estimate help"
                content="Liquidation-quote proceeds minus what you paid for these shares. If this is R0 or less, no profit fee applies."
              />
            </span>
            <span className="font-semibold text-gray-900">{formatCurrency(profitForFee)}</span>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span className="inline-flex items-center gap-1">
              Fee on profit ({formatRateAsPercent(performanceFeeOnProfit)})
              <InfoTooltip
                label="Profit fee help"
                content="A small fee applied only to your profit when you sell. If you sell at a loss, no fee is charged."
              />
            </span>
            <span>− {formatCurrency(feePreview)}</span>
          </div>

          <div className="flex justify-between text-sm font-semibold pt-2 border-t border-gray-200">
            <span className="text-gray-800">You receive (approx.)</span>
            <span className="text-wayja-dark-teal">{formatCurrency(netPreview)}</span>
          </div>

          <p className="text-xs text-gray-400 pt-1 border-t border-gray-200">
            The final amount can change slightly because other people may trade before you. If the price moves too
            much, we&apos;ll ask you to try again so you don&apos;t sell for less than expected.
          </p>
        </div>
      )}

      <div className="flex justify-end gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSelling}
            className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={handleSell}
          disabled={isSellDisabled}
          className={`px-5 py-2.5 text-white rounded-lg transition-all font-medium shadow-sm flex items-center justify-center ${
            isSellDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-wayja-teal hover:bg-wayja-dark-teal active:scale-95'
          }`}
        >
          {isSelling ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : null}
          {isSelling ? 'Selling…' : 'Confirm sell'}
        </button>
      </div>
    </div>
  );
}
